import {
  Button,
  Card,
  Checkbox,
  Col,
  Input,
  Modal,
  Row,
  Spin,
  Table,
  Tabs,
  Typography,
  message,
} from "antd";
import { MobileOutlined } from "@ant-design/icons";
import api from "../../api/axios";
import { useEffect, useState } from "react";
import { getAllPackagesByResellerId, sellPackage } from "../../redux/slices/packageSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { SellPackageRequest } from "../../models/SellPackageRequest";
import {
  formatCurrency,
  formatDataGb,
  formatPlanName,
  getFlagUrl,
  getPlanSortKey,
  isRegionalPlan,
  normalizeDiscountPercent,
} from "./planFormat";

interface Subscriber {
    subscriberId: number;
    imsi: string;
    iccid: string;
    email?: string | null;
  }

const maskChunk = (value: string) => {
  if (!value) return "*";
  if (value.length === 1) return "*";
  if (value.length === 2) return `${value[0]}*`;
  return `${value[0]}${"*".repeat(value.length - 2)}${value[value.length - 1]}`;
};

const maskEmail = (email?: string | null) => {
  const value = (email || "").trim();
  if (!value) return "—";
  const atIndex = value.indexOf("@");
  if (atIndex === -1) return maskChunk(value);
  const local = value.slice(0, atIndex);
  const domain = value.slice(atIndex + 1);
  const [domainName, ...domainParts] = domain.split(".");
  const maskedLocal = maskChunk(local);
  const maskedDomain = maskChunk(domainName || "");
  const suffix = domainParts.length ? `.${domainParts.join(".")}` : "";
  return `${maskedLocal}@${maskedDomain}${suffix}`;
};

const isValidEmail = (value: string) => /^\S+@\S+\.\S+$/.test(value.trim());

  export default function TopupEsimOnly() {
    const dispatch = useDispatch<AppDispatch>();
    const { packages, loading: packagesLoading } = useSelector((state: RootState) => state.packages);
    const { user } = useSelector((state: RootState) => state.auth);
  
    const [sales, setSales] = useState<Subscriber[]>([]);
    const [selectedSim, setSelectedSim] = useState<Subscriber | null>(null);
    const [toppingUpSimId, setToppingUpSimId] = useState<number | null>(null);
    const [search, setSearch] = useState('');
    const [packageSearch, setPackageSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [assigning, setAssigning] = useState(false);
    const [sendTopupEmail, setSendTopupEmail] = useState(false);
    const [topupEmail, setTopupEmail] = useState("");
  
    const fetchSims = async () => {
      setLoading(true);
      try {
        const response = await api.get('/pos/Resellers/getStockSalesSims');
        const sales: Subscriber[] = [];
  
        response.data.salesSims?.forEach((item) => {
          item.imsiList?.forEach((x: any) => {
            sales.push({
              subscriberId: item.subscriberId,
              imsi: x?.imsi,
              iccid: x?.iccid,
              email: x?.email ?? x?.Email ?? item?.email ?? item?.Email ?? null,
            });
          });
        });
  
        setSales(sales);
      } catch (error: any) {
        message.error(error.displayMessage || 'Failed to load eSIMs');
      } finally {
        setLoading(false);
      }
    };
  
    useEffect(() => {
      fetchSims();
      dispatch(getAllPackagesByResellerId());
    }, [dispatch]);

    useEffect(() => {
      if (!selectedSim) {
        setSendTopupEmail(false);
        setTopupEmail("");
        return;
      }

      setSendTopupEmail(false);
      setTopupEmail((selectedSim.email || "").trim());
    }, [selectedSim?.subscriberId]);

    const filteredSims = sales.filter(sim =>
      [sim.imsi, sim.iccid, sim.email, sim.subscriberId.toString()].some(val =>
        val?.toLowerCase().includes(search.toLowerCase())
      )
    );
  
    // const filteredSims = sales.filter(sim =>
    //   [sim.imsi, sim.iccid].some(val => val?.toLowerCase().includes(search.toLowerCase()))
    // );
    const packageSearchValue = packageSearch.toLowerCase();
    const filteredPackages = packages
      .filter(pkg => {
        const rawName = pkg.prepaidPackageTemplateName ?? "";
        const displayName = formatPlanName(rawName);
        return (
          rawName.toLowerCase().includes(packageSearchValue) ||
          displayName.toLowerCase().includes(packageSearchValue)
        );
      })
      .sort((a, b) =>
        getPlanSortKey(a.prepaidPackageTemplateName).localeCompare(
          getPlanSortKey(b.prepaidPackageTemplateName)
      )
    );

    const regionalPackages = filteredPackages.filter((pkg) =>
      isRegionalPlan(pkg.prepaidPackageTemplateName)
    );
    const localPackages = filteredPackages.filter(
      (pkg) => !isRegionalPlan(pkg.prepaidPackageTemplateName)
    );

    const planColumns = [
      {
        title: "Plan",
        dataIndex: "prepaidPackageTemplateName",
        key: "plan",
        render: (value: string) => {
          const displayName = formatPlanName(value);
          const flagUrl = getFlagUrl(value);
          return (
            <span className="plan-table__name">
              {flagUrl ? (
                <img
                  src={flagUrl}
                  alt={`${displayName} flag`}
                  className="plan-table__flag"
                  loading="lazy"
                  onError={(event) => {
                    event.currentTarget.style.display = "none";
                  }}
                />
              ) : null}
              <span className="plan-table__name-text">{displayName}</span>
            </span>
          );
        },
      },
      {
        title: "Data",
        key: "data",
        render: (_: unknown, pkg: any) => formatDataGb(pkg.dataByte),
      },
      {
        title: "Networks Covered",
        key: "networks",
        render: () => "—",
      },
      {
        title: "Type",
        key: "type",
        render: () => (
          <span className="plan-table__type">
            <MobileOutlined className="plan-table__type-icon" />
            eSIM
          </span>
        ),
      },
      {
        title: "Validity",
        key: "validity",
        render: (_: unknown, pkg: any) => `${pkg.periodDays} Days`,
      },
      {
        title: "Retail Price",
        key: "price",
        align: "right" as const,
        render: (_: unknown, pkg: any) =>
          user?.showPrices && Number.isFinite(Number(pkg.cost))
            ? formatCurrency(Number(pkg.cost))
            : "—",
      },
      {
        title: "Your price",
        key: "discountedPrice",
        align: "right" as const,
        render: (_: unknown, pkg: any) => {
          if (!user?.showPrices) return "—";
          const cost = Number(pkg.cost);
          if (!Number.isFinite(cost)) return "—";
          const discount = normalizeDiscountPercent(
            pkg.resellerDiscountPercent ?? pkg.reseller_discount_percent
          );
          const discountedPrice = cost * (1 - discount / 100);
          return formatCurrency(discountedPrice);
        },
      },
      {
        title: "",
        key: "action",
        align: "right" as const,
        render: (_: unknown, pkg: any) => (
          <Button
            type="primary"
            size="small"
            loading={assigning}
            disabled={!selectedSim || toppingUpSimId !== selectedSim.subscriberId}
            onClick={() => handleAssign(pkg.prepaidPackageTemplateId, selectedSim!)}
          >
            Assign
          </Button>
        ),
      },
    ];

    const renderPlansTable = (dataSource: typeof filteredPackages) => (
      <Table
        className="plan-table"
        rowKey="id"
        dataSource={dataSource}
        pagination={{ pageSize: 15, showSizeChanger: false }}
        columns={planColumns}
      />
    );

    const planTabs = [
      {
        key: "local",
        label: `Local (${localPackages.length})`,
        children: renderPlansTable(localPackages),
      },
      {
        key: "regional",
        label: `Regional (${regionalPackages.length})`,
        children: renderPlansTable(regionalPackages),
      },
    ];
  
    const sendTopupConfirmationEmail = async (iccid: string, email: string) => {
      await api.post("/pos/Resellers/esim/sendEmail", {
        iccid,
        email,
        emailType: "TOPUP",
      });
    };

    const handleAssign = async (packageId: string, sim: Subscriber) => {
      const selectedPackage = packages.find(pkg => pkg.prepaidPackageTemplateId === packageId);
      if (!selectedPackage) return;

      const emailToSend = topupEmail.trim();
      const shouldSendEmail = sendTopupEmail;

      if (shouldSendEmail) {
        if (!emailToSend) {
          message.error("Email is required to send TopUp confirmation.");
          return;
        }

        if (!isValidEmail(emailToSend)) {
          message.error("Enter a valid email address.");
          return;
        }
      }

      Modal.confirm({
        title: 'Confirm Package Assignment',
        content: `Are you sure you want to assign "${selectedPackage.prepaidPackageTemplateName}" to IMSI ${sim.imsi}?`,
        okText: 'Yes',
        cancelText: 'No',
        onOk: async () => {
          setAssigning(true);
          try {
            const payload: SellPackageRequest = {
              packageId: selectedPackage.prepaidPackageTemplateId,
              email: null,
              phoneNumber: null,
              iccid: sim.iccid,
              cost: selectedPackage.cost
            };
            await dispatch(sellPackage(payload)).unwrap();
            message.success('TopUp successful');
            if (shouldSendEmail) {
              try {
                await sendTopupConfirmationEmail(sim.iccid, emailToSend);
                message.success("TopUp email sent");
              } catch (error: any) {
                message.error(
                  `TopUp email failed: ${error?.displayMessage || "Unknown error"}`
                );
              }
            }
            setSelectedSim(null);
            setToppingUpSimId(null);
            fetchSims();
          } catch (error: any) {
            message.error(error?.displayMessage || 'TopUp failed');
          } finally {
            setAssigning(false);
          }
        },
      });
    };
  
    return (
      <Row gutter={[16, 16]} className="topup-layout">
        {/* SIM List */}
        <Col xs={24} md={8} className="topup-col topup-col--sims">
          <div className="topup-panel topup-panel--sims">
            <div className="topup-panel__header">
              <Input
                placeholder="Search IMSI, ICCID, email, or subscriber ID"
                value={search}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearch(value);
                  setSelectedSim(null);
                  setToppingUpSimId(null);
                }}
              />
            </div>

            <div className="topup-panel__scroll">
              {loading ? (
                <div className="topup-panel__loading">
                  <Spin />
                </div>
              ) : (
                filteredSims.map(sim => {
                  const isToppingUp = toppingUpSimId === sim.subscriberId;
                  const isDisabled = toppingUpSimId !== null && !isToppingUp;

                  return (
                    <Card
                      size="small"
                      key={`${sim.subscriberId}-${sim.imsi}`}
                      style={{
                        marginBottom: 12,
                        opacity: isDisabled ? 0.5 : 1,
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                      }}
                      actions={
                        isToppingUp
                          ? [<Button onClick={() => { setToppingUpSimId(null); setSelectedSim(null); }}>Cancel</Button>]
                          : [<Button type="primary" onClick={() => { setToppingUpSimId(sim.subscriberId); setSelectedSim(sim); }}>Top Up</Button>]
                      }
                    >
                      <div><strong>IMSI:</strong> {sim.imsi}</div>
                      <div><strong>ICCID:</strong> {sim.iccid}</div>
                      <div><strong>Email:</strong> {maskEmail(sim.email)}</div>
                      <div><strong>SubscriberId:</strong> {sim.subscriberId}</div>
                    </Card>
                  );
                })
              )}
            </div>
          </div>
        </Col>

        {/* Plans */}
        <Col xs={24} md={16} className="topup-col">
          <div className="topup-panel">
            <div className="topup-panel__header">
              <h2 className="topup-panel__title">Available Plans</h2>
              <Input
                placeholder="Search plans..."
                value={packageSearch}
                onChange={(e) => setPackageSearch(e.target.value)}
                className="plan-search"
              />
              <div className="topup-email">
                <Checkbox
                  checked={sendTopupEmail}
                  disabled={!selectedSim || assigning}
                  onChange={(e) => setSendTopupEmail(e.target.checked)}
                >
                  Send TopUp confirmation email
                </Checkbox>
                {sendTopupEmail && (
                  <div className="topup-email__fields">
                    <Input
                      placeholder="user@example.com"
                      value={topupEmail}
                      onChange={(e) => setTopupEmail(e.target.value)}
                      disabled={!selectedSim || assigning}
                    />
                    <Typography.Text type="secondary" className="topup-email__note">
                      This sends a Top-up confirmation email (no QR installation).
                    </Typography.Text>
                  </div>
                )}
              </div>
            </div>

            <div className="topup-panel__scroll">
              {packagesLoading ? (
                <div className="topup-panel__loading">
                  <Spin />
                </div>
              ) : (
                <Tabs className="plan-tabs" defaultActiveKey="local" items={planTabs} />
              )}
            </div>
          </div>
        </Col>
      </Row>
    );
  }
